// Copyright (c) 2026 Otyka LLC. All rights reserved.

import React, { useState, useRef } from 'react';
import { getIdToken } from './auth';
import ArchivePanel from './ArchivePanel';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const COLOR_SCHEMES = [
  { id: 'classic-green', name: 'Classic Green', preview: ['#1a472a', '#52b788', '#c9a84c'] },
  { id: 'navy-gold', name: 'Navy & Gold', preview: ['#0d2b4e', '#1a6eb5', '#d4a843'] },
  { id: 'burgundy-silver', name: 'Burgundy & Silver', preview: ['#6b1a2a', '#c45070', '#9e9e9e'] },
  { id: 'slate-copper', name: 'Slate & Copper', preview: ['#2c3e50', '#546e7a', '#b87333'] },
  { id: 'forest-cream', name: 'Deep Forest', preview: ['#1b3a2d', '#8fbc8f', '#c9a84c'] },
  { id: 'black-gold', name: 'Black & Gold', preview: ['#1a1a1a', '#555555', '#c9a84c'] },
];

const EMPTY_CATEGORY = { id: '', label: '', description: '', icon: '📋' };

export default function TenantForm({ tenant, onSaved, onCancel }) {
  const isEdit = !!tenant;

  const [name, setName] = useState(tenant?.name || '');
  const [slug, setSlug] = useState(tenant?.slug || '');
  const [clubName, setClubName] = useState(tenant?.branding?.clubName || '');
  const [tagline, setTagline] = useState(tenant?.branding?.tagline || '');
  const [colorScheme, setColorScheme] = useState(tenant?.branding?.colorScheme || 'classic-green');
  const [logoUrl, setLogoUrl] = useState(tenant?.branding?.logoUrl || '');
  const [logoMode, setLogoMode] = useState(
    tenant?.branding?.logoUrl?.startsWith('data:') ? 'upload' : 'url'
  );
  const [logoFileName, setLogoFileName] = useState('');
  const fileInputRef = useRef(null);
  const [accessCodes, setAccessCodes] = useState(
    tenant?.accessCodes?.length > 0
      ? tenant.accessCodes
      : tenant?.accessCode
        ? [{ code: tenant.accessCode, label: 'Default', includeInAnalysis: true }]
        : []
  );
  const [categories, setCategories] = useState(
    tenant?.categories?.length > 0
      ? tenant.categories
      : [{ ...EMPTY_CATEGORY }]
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showInsights, setShowInsights] = useState(
    tenant?.settings?.showInsights !== false
  );
  const [profanityFilter, setProfanityFilter] = useState(
    tenant?.settings?.profanityFilter !== false
  );
  const [referenceTerms, setReferenceTerms] = useState(
    (tenant?.settings?.referenceTerms || []).join(', ')
  );
  const [narrativeFocus, setNarrativeFocus] = useState(
    tenant?.settings?.narrativeFocus || ''
  );

  function addCategory() {
    if (categories.length >= 10) return;
    setCategories([...categories, { ...EMPTY_CATEGORY }]);
  }

  function removeCategory(index) {
    setCategories(categories.filter((_, i) => i !== index));
  }

  function updateCategory(index, field, value) {
    const updated = [...categories];
    updated[index] = { ...updated[index], [field]: value };
    // Auto-generate id from label
    if (field === 'label') {
      updated[index].id = value.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-');
    }
    setCategories(updated);
  }

  function handleLogoFile(file) {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file (PNG, JPG, SVG)');
      return;
    }
    if (file.size > 500 * 1024) {
      setError('Image must be under 500 KB');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      setLogoUrl(e.target.result);
      setLogoFileName(file.name);
      setLogoMode('upload');
      setError(null);
    };
    reader.readAsDataURL(file);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    setSaving(true);

    const validCategories = categories.filter((c) => c.label.trim());
    const body = {
      name,
      slug: slug.toLowerCase().replace(/[^a-z0-9-]/g, ''),
      categories: validCategories,
      accessCodes: accessCodes.filter((ac) => ac.code.trim()),
      branding: {
        clubName: clubName || name,
        tagline,
        colorScheme,
        logoUrl: logoUrl || null,
      },
      settings: {
        showInsights,
        profanityFilter,
        narrativeFocus: narrativeFocus.trim() || undefined,
        referenceTerms: referenceTerms
          .split(',')
          .map((t) => t.trim())
          .filter((t) => t.length > 0),
      },
    };

    try {
      const token = await getIdToken();
      const url = isEdit
        ? `${API_BASE}/admin/tenants/${tenant.tenantId}`
        : `${API_BASE}/admin/tenants`;
      const method = isEdit ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || 'Failed to save tenant');
      }

      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="tenant-form">
      <div className="tenant-form__header">
        <h2>{isEdit ? `Edit: ${tenant.name}` : 'Create New Tenant'}</h2>
        <button className="admin-btn admin-btn--secondary" onClick={onCancel}>← Back</button>
      </div>

      <form onSubmit={handleSubmit} className="tenant-form__body">
        {/* Identity */}
        <fieldset className="tenant-form__section">
          <legend>Identity</legend>
          <div className="tenant-form__row">
            <label className="tenant-form__field">
              <span>Organization Name *</span>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} required maxLength={100} />
            </label>
            <label className="tenant-form__field">
              <span>URL Slug *</span>
              <div className="tenant-form__slug-wrap">
                <span className="tenant-form__slug-prefix">/t/</span>
                <input
                  type="text"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                  required
                  maxLength={50}
                  disabled={isEdit}
                  placeholder="my-club"
                />
              </div>
            </label>
          </div>
        </fieldset>

        {/* Access Codes */}
        <fieldset className="tenant-form__section">
          <legend>Access Codes</legend>
          <p style={{ fontSize: '0.8rem', color: '#888', marginBottom: 16 }}>
            Each code can be given to a different group. Codes marked "Include in analysis" will have their feedback appear in insights.
          </p>
          <div className="tenant-form__access-codes">
            {accessCodes.map((ac, i) => (
              <div key={i} className="tenant-form__ac-row">
                <input
                  type="text"
                  value={ac.code}
                  onChange={(e) => {
                    const updated = [...accessCodes];
                    updated[i] = { ...updated[i], code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') };
                    setAccessCodes(updated);
                  }}
                  placeholder="CODE"
                  maxLength={10}
                  className="tenant-form__ac-code"
                />
                <input
                  type="text"
                  value={ac.label}
                  onChange={(e) => {
                    const updated = [...accessCodes];
                    updated[i] = { ...updated[i], label: e.target.value };
                    setAccessCodes(updated);
                  }}
                  placeholder="Label / description (e.g., Golf Members, VIP Group)"
                  maxLength={256}
                  className="tenant-form__ac-label"
                />
                <label className="tenant-form__ac-analysis" title="Include in analysis">
                  <input
                    type="checkbox"
                    checked={ac.includeInAnalysis !== false}
                    onChange={(e) => {
                      const updated = [...accessCodes];
                      updated[i] = { ...updated[i], includeInAnalysis: e.target.checked };
                      setAccessCodes(updated);
                    }}
                  />
                  <span>Analysis</span>
                </label>
                <button
                  type="button"
                  className="tenant-form__ac-remove"
                  onClick={() => setAccessCodes(accessCodes.filter((_, idx) => idx !== i))}
                  aria-label="Remove code"
                >✕</button>
              </div>
            ))}
            <button
              type="button"
              className="admin-btn admin-btn--small admin-btn--secondary"
              onClick={() => setAccessCodes([...accessCodes, { code: '', label: '', includeInAnalysis: true }])}
            >
              + Add Access Code
            </button>
          </div>
        </fieldset>

        {/* Branding */}
        <fieldset className="tenant-form__section">
          <legend>Branding</legend>
          <div className="tenant-form__row">
            <label className="tenant-form__field">
              <span>Display Name</span>
              <input type="text" value={clubName} onChange={(e) => setClubName(e.target.value)} placeholder={name} maxLength={100} />
            </label>
            <label className="tenant-form__field">
              <span>Tagline</span>
              <input type="text" value={tagline} onChange={(e) => setTagline(e.target.value)} placeholder="Your voice shapes the experience" maxLength={100} />
            </label>
          </div>
          {/* Logo — upload or URL */}
          <div className="tenant-form__field">
            <span className="tenant-form__field-label">Logo (optional)</span>
            <div className="tenant-form__logo-tabs">
              <button
                type="button"
                className={`tenant-form__logo-tab${logoMode === 'upload' ? ' tenant-form__logo-tab--active' : ''}`}
                onClick={() => setLogoMode('upload')}
              >
                Upload Image
              </button>
              <button
                type="button"
                className={`tenant-form__logo-tab${logoMode === 'url' ? ' tenant-form__logo-tab--active' : ''}`}
                onClick={() => setLogoMode('url')}
              >
                Provide URL
              </button>
            </div>

            {logoMode === 'upload' ? (
              <div className="tenant-form__logo-upload">
                {logoUrl && logoUrl.startsWith('data:') ? (
                  <div className="tenant-form__logo-preview">
                    <img src={logoUrl} alt="Logo preview" className="tenant-form__logo-img" />
                    <div className="tenant-form__logo-info">
                      <span className="tenant-form__logo-name">{logoFileName || 'Uploaded image'}</span>
                      <button
                        type="button"
                        className="tenant-form__logo-remove"
                        onClick={() => { setLogoUrl(''); setLogoFileName(''); }}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ) : (
                  <div
                    className="tenant-form__logo-dropzone"
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      handleLogoFile(e.dataTransfer.files[0]);
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
                  >
                    <span className="tenant-form__logo-dropzone-icon">🖼️</span>
                    <p><strong>Click to upload</strong> or drag & drop</p>
                    <p className="tenant-form__logo-dropzone-hint">PNG, JPG, SVG · Max 500 KB</p>
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleLogoFile(e.target.files[0])}
                  style={{ display: 'none' }}
                />
              </div>
            ) : (
              <input
                type="url"
                value={logoUrl?.startsWith('data:') ? '' : logoUrl}
                onChange={(e) => setLogoUrl(e.target.value)}
                placeholder="https://example.com/logo.png"
                className="tenant-form__input-full"
              />
            )}

            {logoUrl && (
              <div className="tenant-form__logo-current-preview">
                <img src={logoUrl} alt="Current logo" className="tenant-form__logo-thumb" />
              </div>
            )}
          </div>

          <div className="tenant-form__schemes">
            <span className="tenant-form__field-label">Color Scheme</span>
            <div className="tenant-form__scheme-grid">
              {COLOR_SCHEMES.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  className={`tenant-form__scheme${colorScheme === s.id ? ' tenant-form__scheme--selected' : ''}`}
                  onClick={() => setColorScheme(s.id)}
                >
                  <div className="tenant-form__scheme-swatches">
                    {s.preview.map((c, i) => (
                      <span key={i} style={{ background: c }} className="tenant-form__swatch" />
                    ))}
                  </div>
                  <span>{s.name}</span>
                </button>
              ))}
            </div>
          </div>
        </fieldset>

        {/* Settings */}
        <fieldset className="tenant-form__section">
          <legend>Settings</legend>
          <label className="tenant-form__toggle-field">
            <input
              type="checkbox"
              checked={showInsights}
              onChange={(e) => setShowInsights(e.target.checked)}
              className="tenant-form__checkbox"
            />
            <div className="tenant-form__toggle-text">
              <span className="tenant-form__toggle-label">Show Insights tab</span>
              <span className="tenant-form__toggle-hint">
                When enabled, members can access an aggregated insights dashboard showing sentiment analysis, investment areas, and submission trends.
              </span>
            </div>
          </label>
          <label className="tenant-form__toggle-field" style={{ marginTop: 16 }}>
            <input
              type="checkbox"
              checked={profanityFilter}
              onChange={(e) => setProfanityFilter(e.target.checked)}
              className="tenant-form__checkbox"
            />
            <div className="tenant-form__toggle-text">
              <span className="tenant-form__toggle-label">Profanity filter</span>
              <span className="tenant-form__toggle-hint">
                When enabled, submissions containing inappropriate language are automatically rejected. The user is asked to re-record without profanity.
              </span>
            </div>
          </label>
          <label className="tenant-form__field" style={{ marginTop: 20 }}>
            <span>Reference Terms for People</span>
            <input
              type="text"
              value={referenceTerms}
              onChange={(e) => setReferenceTerms(e.target.value)}
              placeholder="members, guests, patrons"
              maxLength={200}
            />
            <span className="tenant-form__toggle-hint" style={{ marginTop: 4 }}>
              Comma-separated terms used in AI-generated narratives to refer to the people providing feedback (e.g., "members", "customers", "patrons"). The first term is used as the primary reference.
            </span>
          </label>
          <label className="tenant-form__field" style={{ marginTop: 20 }}>
            <span>Narrative Focus</span>
            <textarea
              value={narrativeFocus}
              onChange={(e) => setNarrativeFocus(e.target.value)}
              placeholder="e.g., Emphasize food and beverage quality concerns. Highlight any mentions of staffing shortages or member retention. Downplay cosmetic maintenance items."
              maxLength={2000}
              rows={4}
              className="tenant-form__textarea"
            />
            <span className="tenant-form__toggle-hint" style={{ marginTop: 4 }}>
              Optional guidance (up to 2,000 characters) that shapes the AI-generated executive summary. Use this to direct the narrative toward specific topics, priorities, or areas of concern relevant to your organization. Leave blank for a general-purpose summary.
            </span>
            <span className="tenant-form__char-count" style={{ fontSize: '0.75rem', color: '#888', marginTop: 2 }}>
              {narrativeFocus.length} / 2,000
            </span>
          </label>
        </fieldset>

        {/* Categories */}
        <fieldset className="tenant-form__section">
          <legend>Feedback Categories (max 10)</legend>
          <div className="tenant-form__categories">
            {categories.map((cat, i) => (
              <div key={i} className="tenant-form__cat-row">
                <input
                  type="text"
                  value={cat.icon}
                  onChange={(e) => updateCategory(i, 'icon', e.target.value)}
                  className="tenant-form__cat-icon"
                  maxLength={4}
                  aria-label="Icon"
                />
                <input
                  type="text"
                  value={cat.label}
                  onChange={(e) => updateCategory(i, 'label', e.target.value)}
                  placeholder="Category name"
                  className="tenant-form__cat-label"
                  required
                />
                <input
                  type="text"
                  value={cat.description}
                  onChange={(e) => updateCategory(i, 'description', e.target.value)}
                  placeholder="Short description"
                  className="tenant-form__cat-desc"
                />
                <button type="button" className="tenant-form__cat-remove" onClick={() => removeCategory(i)} aria-label="Remove category">
                  ✕
                </button>
              </div>
            ))}
            {categories.length < 10 && (
              <button type="button" className="admin-btn admin-btn--small admin-btn--secondary" onClick={addCategory}>
                + Add Category
              </button>
            )}
          </div>
        </fieldset>

        {/* Archive (edit mode only) */}
        {isEdit && (
          <fieldset className="tenant-form__section">
            <legend>Data Archiving</legend>
            <ArchivePanel tenantId={tenant.tenantId} tenantName={tenant.name} />
          </fieldset>
        )}

        {error && <div className="tenant-form__error">{error}</div>}

        <div className="tenant-form__actions">
          <button type="button" className="admin-btn admin-btn--secondary" onClick={onCancel}>Cancel</button>
          <button type="submit" className="admin-btn admin-btn--primary" disabled={saving}>
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Tenant'}
          </button>
        </div>
      </form>
    </div>
  );
}
